/**
 * One token system for status coloring across every surface — the StatusChip /
 * StatusBadge, the dispatch timeline blocks, legends and dots. Keyed by the
 * uppercased status with graceful fallbacks. Real semantic intent only (no
 * invented accents); `warning` (amber) and `destructive` (red) stay distinct.
 */
export type StatusChipVariant = 'badge' | 'table' | 'dot';

const STATUS_BADGE_CLASSES: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  CHARGED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  COMPLETED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  CONVERTED: 'bg-teal-100 text-teal-800 border-teal-200',
  PAID: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  RESOLVED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  SENT: 'bg-blue-100 text-blue-800 border-blue-200',
  VIEWED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 border-blue-200',
  INVOICED: 'bg-teal-100 text-teal-800 border-teal-200',
  SCHEDULED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  PROCESSING: 'bg-blue-100 text-blue-800 border-blue-200',
  REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  PAST_DUE: 'bg-amber-100 text-amber-800 border-amber-200',
  PARTIAL: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  PARTIALLY_REFUNDED: 'bg-amber-100 text-amber-800 border-amber-200',
  PENDING: 'bg-gray-100 text-gray-700 border-gray-200',
  FOLLOW_UP: 'bg-amber-100 text-amber-800 border-amber-200',
  ON_HOLD: 'bg-amber-100 text-amber-800 border-amber-200',
  SUSPENDED: 'bg-destructive/10 text-destructive border-destructive/20',
  PAUSED: 'bg-purple-100 text-purple-800 border-purple-200',
  REVISED: 'bg-purple-100 text-purple-800 border-purple-200',
  DECLINED: 'bg-destructive/10 text-destructive border-destructive/20',
  DISPUTED: 'bg-amber-100 text-amber-800 border-amber-200',
  DISPUTE_UNDER_REVIEW: 'bg-amber-100 text-amber-800 border-amber-200',
  EXPIRED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  FAILED: 'bg-amber-100 text-amber-800 border-amber-200',
  OVERDUE: 'bg-amber-100 text-amber-800 border-amber-200',
  CLEARING: 'bg-amber-100 text-amber-800 border-amber-200',
  REVERSED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  REFUNDED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  CANCELLED: 'bg-destructive/10 text-destructive border-destructive/20',
  CHARGEBACK_LOST: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  CHARGEBACK_WON: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  CLOSED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  DRAFT: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  INACTIVE: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  SKIPPED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  VOID: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  WRITTEN_OFF: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  OPEN: 'bg-blue-100 text-blue-800 border-blue-200',
  SUCCEEDED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800 border-amber-200',
  UNSCHEDULED: 'bg-amber-100 text-amber-800 border-amber-200',
  APPLIED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  ARCHIVED: 'bg-zinc-200 text-zinc-700 border-zinc-200',
  DISPATCHED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  EN_ROUTE: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  ARRIVED: 'bg-sky-100 text-sky-800 border-sky-200',
  NO_SHOW: 'bg-orange-100 text-orange-800 border-orange-200',
  RESCHEDULED: 'bg-purple-100 text-purple-800 border-purple-200',
  RESERVED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  CANCELLATION_PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
};

const STATUS_TABLE_CLASSES: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  CHARGED: 'bg-emerald-100 text-emerald-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  CONVERTED: 'bg-emerald-100 text-emerald-800',
  PAID: 'bg-emerald-100 text-emerald-800',
  RESOLVED: 'bg-emerald-100 text-emerald-800',
  SENT: 'bg-blue-100 text-blue-800',
  VIEWED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  INVOICED: 'bg-blue-100 text-blue-800',
  SCHEDULED: 'bg-indigo-100 text-indigo-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  REVIEW: 'bg-amber-100 text-amber-800',
  PAST_DUE: 'bg-amber-100 text-amber-800',
  PARTIAL: 'bg-amber-100 text-amber-800',
  PARTIALLY_REFUNDED: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-amber-100 text-amber-800',
  FOLLOW_UP: 'bg-amber-100 text-amber-800',
  ON_HOLD: 'bg-amber-100 text-amber-800',
  SUSPENDED: 'bg-destructive/10 text-destructive',
  PAUSED: 'bg-purple-100 text-purple-800',
  REVISED: 'bg-purple-100 text-purple-800',
  DECLINED: 'bg-destructive/10 text-destructive',
  DISPUTED: 'bg-amber-100 text-amber-800',
  DISPUTE_UNDER_REVIEW: 'bg-amber-100 text-amber-800',
  EXPIRED: 'bg-zinc-200 text-zinc-700',
  FAILED: 'bg-amber-100 text-amber-800',
  OVERDUE: 'bg-amber-100 text-amber-800',
  CLEARING: 'bg-amber-100 text-amber-800',
  REVERSED: 'bg-zinc-200 text-zinc-700',
  REFUNDED: 'bg-zinc-200 text-zinc-700',
  CANCELLED: 'bg-destructive/10 text-destructive',
  CHARGEBACK_LOST: 'bg-zinc-200 text-zinc-700',
  CHARGEBACK_WON: 'bg-zinc-200 text-zinc-700',
  CLOSED: 'bg-zinc-200 text-zinc-700',
  DRAFT: 'bg-zinc-200 text-zinc-700',
  INACTIVE: 'bg-zinc-200 text-zinc-700',
  SKIPPED: 'bg-zinc-200 text-zinc-700',
  VOID: 'bg-zinc-200 text-zinc-700',
  WRITTEN_OFF: 'bg-zinc-200 text-zinc-700',
  OPEN: 'bg-blue-100 text-blue-800',
  SUCCEEDED: 'bg-emerald-100 text-emerald-800',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  UNSCHEDULED: 'bg-amber-100 text-amber-800',
  APPLIED: 'bg-zinc-200 text-zinc-700',
  ARCHIVED: 'bg-zinc-200 text-zinc-700',
  DISPATCHED: 'bg-indigo-100 text-indigo-800',
  EN_ROUTE: 'bg-cyan-100 text-cyan-800',
  ARRIVED: 'bg-sky-100 text-sky-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  RESCHEDULED: 'bg-purple-100 text-purple-800',
  RESERVED: 'bg-indigo-100 text-indigo-800',
  CONFIRMED: 'bg-emerald-100 text-emerald-800',
  CANCELLATION_PENDING: 'bg-amber-100 text-amber-800',
};

const STATUS_DOT_CLASSES: Record<string, string> = {
  ACTIVE: 'bg-emerald-500',
  APPROVED: 'bg-emerald-500',
  CHARGED: 'bg-emerald-500',
  COMPLETED: 'bg-emerald-500',
  CONVERTED: 'bg-teal-500',
  PAID: 'bg-emerald-500',
  RESOLVED: 'bg-emerald-500',
  SENT: 'bg-blue-500',
  VIEWED: 'bg-indigo-500',
  IN_PROGRESS: 'bg-blue-500',
  INVOICED: 'bg-teal-500',
  SCHEDULED: 'bg-indigo-500',
  PROCESSING: 'bg-blue-500',
  REVIEW: 'bg-yellow-500',
  PAST_DUE: 'bg-amber-500',
  PARTIAL: 'bg-yellow-500',
  PARTIALLY_REFUNDED: 'bg-amber-500',
  PENDING: 'bg-gray-400',
  FOLLOW_UP: 'bg-amber-500',
  ON_HOLD: 'bg-amber-500',
  SUSPENDED: 'bg-destructive',
  PAUSED: 'bg-purple-500',
  REVISED: 'bg-purple-500',
  DECLINED: 'bg-destructive',
  DISPUTED: 'bg-amber-500',
  DISPUTE_UNDER_REVIEW: 'bg-amber-500',
  EXPIRED: 'bg-zinc-500',
  FAILED: 'bg-amber-500',
  OVERDUE: 'bg-amber-500',
  CLEARING: 'bg-amber-500',
  REVERSED: 'bg-zinc-500',
  REFUNDED: 'bg-zinc-500',
  CANCELLED: 'bg-destructive',
  CHARGEBACK_LOST: 'bg-zinc-500',
  CHARGEBACK_WON: 'bg-zinc-500',
  CLOSED: 'bg-zinc-500',
  DRAFT: 'bg-zinc-500',
  INACTIVE: 'bg-zinc-500',
  SKIPPED: 'bg-zinc-500',
  VOID: 'bg-zinc-500',
  WRITTEN_OFF: 'bg-zinc-500',
  OPEN: 'bg-blue-500',
  SUCCEEDED: 'bg-emerald-500',
  PENDING_APPROVAL: 'bg-amber-500',
  UNSCHEDULED: 'bg-amber-500',
  APPLIED: 'bg-zinc-500',
  ARCHIVED: 'bg-zinc-500',
  DISPATCHED: 'bg-indigo-500',
  EN_ROUTE: 'bg-cyan-500',
  ARRIVED: 'bg-sky-500',
  NO_SHOW: 'bg-orange-500',
  RESCHEDULED: 'bg-purple-500',
  RESERVED: 'bg-indigo-500',
  CONFIRMED: 'bg-emerald-500',
  CANCELLATION_PENDING: 'bg-amber-500',
};

export function getStatusChipClasses(status: string, variant: StatusChipVariant): string {
  const normalizedStatus = status.toUpperCase();
  if (variant === 'table') {
    return STATUS_TABLE_CLASSES[normalizedStatus] ?? 'bg-zinc-200 text-zinc-700';
  }
  return STATUS_BADGE_CLASSES[normalizedStatus] ?? 'bg-gray-100 text-gray-700 border-gray-200';
}

/** Tint + border classes for a status block (dispatch timeline events). */
export function getStatusBlockClasses(status: string): string {
  return STATUS_BADGE_CLASSES[status.toUpperCase()] ?? 'bg-gray-100 text-gray-700 border-gray-200';
}

/** Dot fill class for a status (legends, list dots, timeline markers). */
export function getStatusDotClass(status: string): string {
  return STATUS_DOT_CLASSES[status.toUpperCase()] ?? 'bg-gray-400';
}

/**
 * Light pastel tint (bg + border) for a dense timeline block — calm, dark-text
 * cards that carry status via a light wash + the dot, not a saturated fill.
 * `!important` so it beats FullCalendar's inline event background. Full literal
 * class strings (never interpolated) so Tailwind's JIT keeps them.
 */
const STATUS_TINT_BY_FAMILY = {
  emerald: '!bg-emerald-50 !border-emerald-200 dark:!bg-emerald-950/40 dark:!border-emerald-900',
  teal: '!bg-teal-50 !border-teal-200 dark:!bg-teal-950/40 dark:!border-teal-900',
  blue: '!bg-blue-50 !border-blue-200 dark:!bg-blue-950/40 dark:!border-blue-900',
  indigo: '!bg-indigo-50 !border-indigo-200 dark:!bg-indigo-950/40 dark:!border-indigo-900',
  yellow: '!bg-yellow-50 !border-yellow-200 dark:!bg-yellow-950/40 dark:!border-yellow-900',
  amber: '!bg-amber-50 !border-amber-200 dark:!bg-amber-950/40 dark:!border-amber-900',
  gray: '!bg-gray-50 !border-gray-200 dark:!bg-gray-900/50 dark:!border-gray-800',
  purple: '!bg-purple-50 !border-purple-200 dark:!bg-purple-950/40 dark:!border-purple-900',
  pink: '!bg-pink-50 !border-pink-200 dark:!bg-pink-950/40 dark:!border-pink-900',
  zinc: '!bg-zinc-50 !border-zinc-200 dark:!bg-zinc-900/50 dark:!border-zinc-800',
  cyan: '!bg-cyan-50 !border-cyan-200 dark:!bg-cyan-950/40 dark:!border-cyan-900',
  sky: '!bg-sky-50 !border-sky-200 dark:!bg-sky-950/40 dark:!border-sky-900',
  orange: '!bg-orange-50 !border-orange-200 dark:!bg-orange-950/40 dark:!border-orange-900',
  destructive: '!bg-destructive/10 !border-destructive/20 dark:!bg-destructive/20',
} as const;

/** Token family key shared by the tint + bar helpers (NOT-329 color-by modes). */
export type TintFamily = keyof typeof STATUS_TINT_BY_FAMILY;

/** Map a booking status to its tint family (exported for NOT-329 color-by). */
export function statusTintFamily(status: string): TintFamily {
  return STATUS_TINT_FAMILY[status.toUpperCase()] ?? 'zinc';
}

const STATUS_TINT_FAMILY: Record<string, TintFamily> = {
  ACTIVE: 'emerald',
  APPROVED: 'emerald',
  CHARGED: 'emerald',
  COMPLETED: 'emerald',
  PAID: 'emerald',
  RESOLVED: 'emerald',
  SUCCEEDED: 'emerald',
  CONFIRMED: 'emerald',
  CONVERTED: 'teal',
  INVOICED: 'teal',
  SENT: 'blue',
  IN_PROGRESS: 'blue',
  PROCESSING: 'blue',
  OPEN: 'blue',
  VIEWED: 'indigo',
  SCHEDULED: 'indigo',
  DISPATCHED: 'indigo',
  RESERVED: 'indigo',
  REVIEW: 'yellow',
  PARTIAL: 'yellow',
  PAST_DUE: 'amber',
  PARTIALLY_REFUNDED: 'amber',
  FOLLOW_UP: 'amber',
  ON_HOLD: 'amber',
  SUSPENDED: 'amber',
  DISPUTED: 'amber',
  DISPUTE_UNDER_REVIEW: 'amber',
  FAILED: 'amber',
  OVERDUE: 'amber',
  CLEARING: 'amber',
  PENDING_APPROVAL: 'amber',
  UNSCHEDULED: 'amber',
  CANCELLATION_PENDING: 'amber',
  PENDING: 'gray',
  PAUSED: 'purple',
  REVISED: 'purple',
  RESCHEDULED: 'purple',
  DECLINED: 'destructive',
  CANCELLED: 'destructive',
  EXPIRED: 'zinc',
  REVERSED: 'zinc',
  REFUNDED: 'zinc',
  CHARGEBACK_LOST: 'zinc',
  CHARGEBACK_WON: 'zinc',
  CLOSED: 'zinc',
  DRAFT: 'zinc',
  INACTIVE: 'zinc',
  SKIPPED: 'zinc',
  VOID: 'zinc',
  WRITTEN_OFF: 'zinc',
  APPLIED: 'zinc',
  ARCHIVED: 'zinc',
  EN_ROUTE: 'cyan',
  ARRIVED: 'sky',
  NO_SHOW: 'orange',
};

export function getStatusBlockTint(status: string): string {
  return tintByFamily(statusTintFamily(status));
}

/**
 * Left status bar (~4px) for a dense timeline block — communicates status by a
 * saturated edge stripe so the dot can be dropped. Same JIT-safe literal-class
 * pattern as the tint; `!important` so it beats FullCalendar's inline borders.
 */
const STATUS_BAR_BY_FAMILY = {
  emerald: '!border-l-emerald-400',
  teal: '!border-l-teal-400',
  blue: '!border-l-blue-400',
  indigo: '!border-l-indigo-400',
  yellow: '!border-l-yellow-400',
  amber: '!border-l-amber-400',
  gray: '!border-l-gray-400',
  purple: '!border-l-purple-400',
  pink: '!border-l-pink-400',
  zinc: '!border-l-zinc-400',
  cyan: '!border-l-cyan-400',
  sky: '!border-l-sky-400',
  orange: '!border-l-orange-400',
  destructive: '!border-l-destructive',
} as const;

/**
 * NOT-329 — generic tint/bar helpers keyed by family (not status), so the
 * color-by modes (job type / priority / confirmation / tech) can tint by their
 * own dimension while the left bar still encodes status. The status-keyed
 * helpers below delegate to these.
 */
export function tintByFamily(family: TintFamily): string {
  return STATUS_TINT_BY_FAMILY[family];
}

export function barByFamily(family: TintFamily): string {
  return `!border-l-4 ${STATUS_BAR_BY_FAMILY[family]}`;
}

export function getStatusBarClass(status: string): string {
  return barByFamily(statusTintFamily(status));
}
